
/**
 *
 * @param {Object} Options object
 */
const slider = function({
  container = '.slider',
  responsive = {
    0: {
      items: 1,
      gutter: 20,
    },
    576: {
      items: 2,
      gutter: 20,
    },
    768: {
      items: 3,
      gutter: 20,
    },
    992: {
      items: 4,
      gutter: 20,
    },
  },
  hideBtnClass = 'hidden',
} = {}) {
  const timeoutLength = 100;

  /**
   * Returns the current responsive options based on the current viewport
   *
   * @return {Object}
   */
  const getResponsiveOptions = function() {
    const x = window.visualViewport.width;
    let responsiveItems = Object.keys(responsive).filter((key) => key < x);
    responsiveItems = responsiveItems.map((i) => Number(i));
    const key = Math.max(...responsiveItems);
    return responsive[key];
  };

  /**
   * Debounce function
   *
   * @param {Function} func
   * @param {Number} delay
   * @return {Function}
   */
  const debounce = (func, delay) => {
    let timeoutId;

    return function(...args) {
      const context = this;

      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(context, args);
      }, delay);
    };
  };

  /**
   * Calculate the width of an item based on the available space and the number
   * of items to show (excluding the css gap spacing)
   *
   * @param {Number} width - the visible width of the slider
   * @param {Number} itemsToShow - the number of items that should be visible
   * @param {Number} gap - this css gap property width
   * @return {Number}
   */
  const getItemSize = function(width, itemsToShow, gap) {
    const tempWidth = width - (gap * (itemsToShow - 1));
    return tempWidth / itemsToShow;
  };

  /**
   *
   * @param {HTMLElement} container
   * @param {NodeList} elements
   * @param {Number} itemsToShow
   * @param {Number} gap
   * @param {HTMLElement} buttons slider controls
   * @param {Function} callbackFn callback function to run
   */
  const setItemSize = function(container, elements,
      itemsToShow, gap, buttons, callbackFn) {
    const width = getItemSize(container.clientWidth, itemsToShow, gap);
    container.style.gap = '0 ' + gap + 'px';
    elements.forEach((item) => {
      item.style.flexBasis = width + 'px';
    });

    if (container.clientWidth >= width * elements.length) {
      buttons.classList.add(hideBtnClass);
    } else {
      buttons.classList.remove(hideBtnClass);
    }

    callbackFn();
  };

  /**
   * Takes a container element and a set of slider elements (which should all be
   * the same width). From this we can calculate the width of the container and
   * we can then work out what the nearest next or previous item if we are to
   * scroll the slider by the width of the container.
   *
   * @param {HTMLElement} container
   * @param {NodeList} elements
   * @param {String} direction
   * @param {Number} gutter need the gutter width
   * @return {Number}
   */
  function getScrollTo(container, elements, direction, gutter) {
    let scrollPosition = container.scrollLeft;
    const elementWidth = elements[0].clientWidth + gutter;

    const positions = [];

    for (let index = 0; index < elements.length; index++) {
      positions.push(elementWidth * index);
    }

    let scrollTo = 0;
    let filteredPositions;

    switch (direction) {
      case 'next':
        // eslint-disable-next-line max-len
        scrollPosition = scrollPosition + container.clientWidth - gutter;
        filteredPositions = positions.filter((i) => i >= scrollPosition);
        if (filteredPositions.length == 0) {
          scrollTo = scrollPosition;
        } else {
          scrollTo = Math.min(...filteredPositions);
        }
        break;

      case 'previous':
        scrollPosition = scrollPosition - container.clientWidth + gutter;
        filteredPositions = positions.filter((i) => i <= scrollPosition);
        if (filteredPositions.length == 0) {
          scrollTo = 0;
        } else {
          scrollTo = Math.max(...filteredPositions);
        }
        break;

      default:
        break;
    }

    return scrollTo;
  }

  /**
   * Get a list of all the slider items on the page
   */
  const sliders = document.querySelectorAll(container);

  /**
   * Initialise sliders from a NodeList based on the current selector
   */
  sliders.forEach((slider) => {
    // needed to set up the debounced function inside here for context to work
    // correctly
    const debouncedSetItemSize = debounce(setItemSize, timeoutLength);

    // get the inner slider item
    const sliderInner = slider.querySelector('.slider-inner');
    const els = sliderInner.querySelectorAll('.slider-item');

    const buttons = slider.querySelector('.slider-nav');
    const prevButton = slider.querySelector('.slider-prev');
    const nextButton = slider.querySelector('.slider-next');

    const checkButtons = function() {
      if (sliderInner.scrollLeft < 50) {
        prevButton.setAttribute('disabled', true);
      } else {
        prevButton.removeAttribute('disabled');
      }

      const scrollMax = sliderInner.scrollWidth - sliderInner.clientWidth;

      if (sliderInner.scrollLeft >= scrollMax - 50) {
        nextButton.setAttribute('disabled', true);
      } else {
        nextButton.removeAttribute('disabled');
      }
    };

    // add resize listener
    window.addEventListener(
        'resize',
        function() {
          const opts = getResponsiveOptions();
          debouncedSetItemSize(
              sliderInner, els,
              opts['items'], opts['gutter'],
              buttons, checkButtons);
        });

    // add window load listener
    window.addEventListener(
        'load', function() {
          const opts = getResponsiveOptions();
          setItemSize(
              sliderInner, els,
              opts['items'], opts['gutter'],
              buttons, checkButtons);
          checkButtons();
        });

    /**
     * Generic function to drive the slide. Just needs a direction (previous or
     * next) and will do the rest of the heavy lifting.
     *
     * @param {String} direction
     */
    const slide = function(direction) {
      const els = sliderInner.querySelectorAll('.slider-item');
      const opts = getResponsiveOptions();
      const scrollPosition = getScrollTo(
          sliderInner, els, direction, opts['gutter']);
      sliderInner.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    };

    /**
     * Add scroll listener to the slider
     */
    sliderInner.addEventListener('scroll', (event) => {
      clearTimeout(window.scrollEndTimer);
      window.scrollEndTimer = setTimeout(
          function() {
            checkButtons();
          }, timeoutLength);
    });

    const moveToPrevious = function(event) {
      slide('previous');
    };

    const moveToNext = function(event) {
      slide('next');
    };

    /**
     * Wire up the button click event listener
     */
    prevButton.addEventListener('click', moveToPrevious);

    /**
     * Wire up the button click event listener
     */
    nextButton.addEventListener('click', moveToNext);

    /**
     * Adding keyboard events.
     * TODO: add and remove event listener only when the slider is 'active'
     */
    // document.addEventListener(
    //     'keydown',
    //     (event) => {
    //       const keyName = event.key;
    //       if (document.activeElement.closest('.slider') == slider) {
    //         switch (keyName) {
    //           case 'ArrowRight':
    //             moveToNext();
    //             break;
    //           case 'ArrowLeft':
    //             moveToPrevious();
    //             break;

    //           default:
    //             break;
    //         }
    //       }
    //     },
    //     false
    // );
  });
};

export default slider;
